import PropTypes from 'prop-types';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { alpha, styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import NavItem from '../NavItem';
import Transitions from 'ui-component/extended/Transitions';
import { useCloudRestApiAccess } from 'contexts/CloudRestApiAccessContext';
import { isSupportAgentTokenAccessRestricted } from 'views/pages/administration/cloudRestApiTokenAccess';

import { useGetMenuMaster } from 'api/menu';
import { MenuOrientation, ThemeDirection, ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';
import useMenuCollapse from 'hooks/useMenuCollapse';

// third party
import { FormattedMessage } from 'react-intl';

// assets
import { IconChevronDown, IconChevronRight, IconChevronUp } from '@tabler/icons-react';

// horizontal-menu - wrapper
const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: 'visible',
  zIndex: 1202,
  minWidth: 180,
  '&:before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 34,
    left: -5,
    width: 12,
    height: 12,
    transform: 'translateY(-50%) rotate(45deg)',
    zIndex: 120,
    borderWidth: '6px',
    borderStyle: 'solid',
    borderColor: `transparent transparent ${theme.palette.background.paper}  ${theme.palette.background.paper}`
  },
  '&[data-popper-placement="left-start"]:before': {
    left: 'auto',
    right: -5,
    borderColor: `${theme.palette.background.paper}  ${theme.palette.background.paper} transparent transparent`
  },
  '&[data-popper-placement="left-end"]:before': {
    top: 'auto',
    bottom: 15,
    left: 'auto',
    right: -5,
    borderColor: `${theme.palette.background.paper}  ${theme.palette.background.paper} transparent transparent`
  },
  '&[data-popper-placement="right-end"]:before': {
    top: 'auto',
    bottom: 15
  }
}));

export default function NavCollapse({ menu, level, parentId }) {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const ref = useRef(null);

  const { mode, menuOrientation, borderRadius, themeDirection } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { loading: cloudRestLoading, enabled: cloudRestEnabled } = useCloudRestApiAccess();
  // PRJCBUGEN-63941: hide the Partner Cloud REST API menu link while a support agent is
  // impersonating a customer — CloudRestApiGate also blocks direct navigation to the route.
  const showCloudRestMenuItem = !cloudRestLoading && cloudRestEnabled && !isSupportAgentTokenAccessRestricted();

  const filteredChildren = useMemo(() => {
    const kids = menu.children || [];
    return kids.filter((c) => !c.cloudRestApiGate || showCloudRestMenuItem);
  }, [menu.children, showCloudRestMenuItem]);
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  const usePopperForThisMenu = Boolean(menu?.flyout) && !downMD;
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClickMini = (event) => {
    setAnchorEl(null);

    if (!usePopperForThisMenu) {
      setOpen((prev) => !prev);
      setSelected((prev) => (prev ? null : menu.id));
    } else {
      setAnchorEl(event?.currentTarget);
    }
  };

  const handleHover = (event) => {
    setAnchorEl(event?.currentTarget);
  };

  const openMini = Boolean(anchorEl);

  const handleMiniClose = () => {
    setAnchorEl(null);
  };

  const handleClosePopper = () => {
    setOpen(false);
    if (!openMini) {
      if (!menu.url) {
        setSelected(null);
      }
    }
    setAnchorEl(null);
  };

  const { pathname } = useLocation();

  // menu collapse for sub-levels
  useMenuCollapse(menu, pathname, openMini, setSelected, setOpen, setAnchorEl);

  const [hoverStatus, setHover] = useState(false);

  const compareSize = () => {
    const compare = ref.current && ref.current.scrollWidth > ref.current.clientWidth;
    setHover(compare);
  };

  useEffect(() => {
    compareSize();
    window.addEventListener('resize', compareSize);
    window.removeEventListener('resize', compareSize);
  }, []);

  useEffect(() => {
    // Auto-select parent when any of its children is active
    if (filteredChildren?.length) {
      const childActive = filteredChildren.some((child) => {
        const path = child.link ? child.link : child.url;
        return path && pathname.startsWith(path);
      });

      if (childActive) {
        setSelected(menu.id);
      } else if (!usePopperForThisMenu && menu.url && pathname === menu.url) {
        setSelected(menu.id);
        setOpen(true);
      } else {
        setSelected(null);
      }
    }
  }, [pathname, menu, usePopperForThisMenu, filteredChildren]);

  // menu collapse & item
  const menus = filteredChildren?.map((item) => {
    switch (item.type) {
      case 'collapse':
        return <NavCollapse key={item.id} menu={item} level={level + 1} parentId={parentId} />;
      case 'item':
        return <NavItem key={item.id} item={item} level={level + 1} />;
      case 'subheader':
        if (downMD) return null; // hide subheader in mobile view to save space
        return (
          <Typography
            key={item.id}
            variant="caption"
            sx={{
              display: 'block',
              px: 1.5,
              pt: 1,
              pb: 1.5,
              color: 'text.secondary',
            }}
          >
            {item.title ? <FormattedMessage id={item.title} /> : ''}
          </Typography>
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error">
            Menu Items Error
          </Typography>
        );
    }
  });

  const isSelected = selected === menu.id;

  const Icon = menu.icon;
  const menuIcon = menu.icon ? (
    <Icon strokeWidth={1.5} size={drawerOpen ? '20px' : '24px'} />
  ) : null;

  const collapseIcon = drawerOpen && !usePopperForThisMenu ? (
    <IconChevronUp stroke={1.5} size="16px" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
  ) : (
    <IconChevronRight stroke={1.5} size="16px" className='IconChevronNotOpenDrawer' style={{ marginTop: 'auto', marginBottom: 'auto' }} />
  );

  const iconSelectedColor = mode === ThemeMode.DARK && drawerOpen ? 'text.primary' : '';
  const popperId = openMini ? `collapse-pop-${menu.id}` : undefined;

  const [isHovered, setIsHovered] = useState(false);

  const active = isHovered || openMini;


  // Reset hover highlight when route changes to avoid lingering grey background
  useEffect(() => {
    if (isHovered) setIsHovered(false);
  }, [pathname]);

  return (
    <>
      {!isHorizontal ? (
        <>
          <Tooltip title={menu.title ? <FormattedMessage id={menu.title} /> : ''}
            placement="right"
            arrow
            disableHoverListener={drawerOpen}
            disableFocusListener={drawerOpen}
            disableTouchListener={drawerOpen}
            disableInteractive
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 3],
                  },
                },
              ],
            }}
          >
          <ListItemButton
            sx={{
              zIndex: 1201,
              borderRadius: `${borderRadius}px`,
              mb: 0.5,
              padding: '7px 12px',
                ...(drawerOpen && level !== 1 && {
                  ml: downMD
                    ? level === 2
                      ? '12px'
                      : '24px'
                    : `${level * 18}px`
                }),
              ...(!drawerOpen && { pl: 1.25 }),
              ...(drawerOpen && level === 1 && { pl: 1.5 }),
                ...(level === 1 && {
                  ml: downMD ? 0 : drawerOpen ? 0 : 0,
                  ...(downMD && {
                    width: '100%',
                    justifyContent: 'flex-start'
                  })
                }),
              ...(drawerOpen &&
                level === 1 &&
                mode !== ThemeMode.DARK && {
                '&:hover': { bgcolor: 'secondary.light' },
                '&.Mui-selected': {
                  // bgcolor: usePopperForThisMenu ? 'grey.200' : 'grey.200',
                  color: iconSelectedColor,
                  '&:hover': { color: iconSelectedColor}
                }
              }),
              ...((!drawerOpen || level !== 1) && {
                py: level === 1 ? 0.5 : 0.6,
                '&:hover': { bgcolor: 'transparent' },
                '&.Mui-selected': {
                  ...(usePopperForThisMenu && { color: iconSelectedColor })
                }
              }),
              // only for the first level parent items when drawer is closed, apply selected styles; for child items, keep background transparent even when selected
              ...(!drawerOpen && level === 1 && {
                width: 42,
                height: 42,
                margin:'0 auto 4px 15px',
                paddingLeft:'4px',
                position: 'relative',
                 '& .IconChevronNotOpenDrawer': {
                    position: 'absolute',
                    right: -4,
                  },
              }),
              ...(active && {
                bgcolor: mode === ThemeMode.DARK ? alpha(theme.palette.white.paper, 0.08) : 'grey.200',
                '&:hover': { bgcolor: mode === ThemeMode.DARK ? alpha(theme.palette.white.paper, 0.08) : 'grey.200', }
              })
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            selected={isSelected}
            // {...((!drawerOpen || usePopperForThisMenu) && { onMouseEnter: handleClickMini, onMouseLeave: handleMiniClose })}
            onClick={handleClickMini}
          >
            {menuIcon && (
              <ListItemIcon
                sx={{
                  minWidth: level === 1 ? 32 : 0,
                  ...(!drawerOpen &&
                    level === 1 && {
                    borderRadius: `${borderRadius}px`,
                    width: 18,
                    height: 46,
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      bgcolor: mode === ThemeMode.DARK ? 'transparent' : 'transparent',
                    },
                    ...((isSelected || anchorEl) && {
                      bgcolor: mode === ThemeMode.DARK ? 'transparent' : 'transparent',
                      '&:hover': {
                        bgcolor: mode === ThemeMode.DARK ? 'transparent' : 'transparent',
                      }
                    })
                  })
                }}
              >
                {menuIcon}
              </ListItemIcon>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <Tooltip title={menu.title ? <FormattedMessage id={menu.title} /> : ''} disableHoverListener={!hoverStatus}>
                <ListItemText
                  primary={
                    <Typography
                      ref={ref}
                      noWrap
                      variant={isSelected || anchorEl ? 'body1' : 'body1'}

                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: 121,
                        ...(themeDirection === ThemeDirection.RTL && { textAlign: 'end', direction: 'rtl' })
                      }}
                    >
                      {menu.title ? <FormattedMessage id={menu.title} /> : ''}
                    </Typography>
                  }
                  secondary={
                    menu.caption && (
                      <Typography variant="caption" gutterBottom sx={{ display: 'block', ...theme.typography.subMenuCaption }}>
                        <FormattedMessage id={menu.caption} />
                      </Typography>
                    )
                  }
                />
              </Tooltip>
            )}

            {openMini || open ? (
              collapseIcon
            ) : (
              <IconChevronRight stroke={1.5} size="16px" className='IconChevronNotOpenDrawer' style={{ marginTop: 'auto', marginBottom: 'auto' }} />
            )}

            {(!drawerOpen || usePopperForThisMenu) && (
              <Popper
                open={openMini}
                anchorEl={anchorEl}
                placement="right"
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 17]
                    }
                  }
                ]}
                sx={{
                  overflow: 'visible',
                  zIndex: 2001,
                  minWidth: 180,
                }}
              >
                {({ TransitionProps }) => (
                  <Transitions in={openMini} {...TransitionProps}>
                    <Paper
                      onMouseLeave={handleClosePopper}
                      sx={{
                        position: 'relative',
                        overflow: 'visible',
                        borderRadius: 2,
                        boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.12)',
                        backgroundImage: 'none',
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          left: -8,
                          top: '50%',
                          width: 0,
                          height: 0,
                          transform: 'translateY(-50%)',
                          borderTop: '8px solid transparent',
                          borderBottom: '8px solid transparent',
                          borderRight: (theme) => `8px solid ${theme.palette.background.paper}`
                        },
                        '&:after': {
                          content: '""',
                          position: 'absolute',
                          left: -9,
                          top: '50%',
                          width: 0,
                          height: 0,
                          transform: 'translateY(-50%)',
                          borderTop: '9px solid transparent',
                          borderBottom: '9px solid transparent',
                          borderRight: (theme) => `9px solid rgba(0,0,0,0.06)`
                        }
                      }}
                    >
                      <ClickAwayListener onClickAway={handleClosePopper}>
                        <Box sx={{ minWidth: 255, py: 1, px: 1 }}>
                          <Typography variant="caption" sx={{ px: 1.5, pt: 0.5, pb: 1, color: 'text.secondary', display: 'block' }}>
                            {menu.title ? <FormattedMessage id={menu.title} /> : ''}
                          </Typography>
                          <List
                            disablePadding
                            sx={{
                              '& .MuiListItemIcon-root': { display: 'none' },
                              '& .MuiListItemButton-root': {
                                mx: 1,
                                my: 0.5,
                                borderRadius: 2,
                                transition: (theme) =>
                                  theme.transitions.create(['background-color', 'transform'], {
                                    duration: theme.transitions.duration.shorter
                                  }),
                                '&:hover': {
                                  bgcolor: mode === ThemeMode.DARK ? alpha(theme.palette.white.paper, 0.08) : 'grey.200',
                                }
                              }
                            }}
                          >
                            {menus}
                          </List>
                        </Box>
                      </ClickAwayListener>
                    </Paper>
                  </Transitions>
                )}
              </Popper>
            )}
          </ListItemButton>
          </Tooltip>
          {!usePopperForThisMenu && (
            <Collapse in={open} timeout="auto" unmountOnExit>
              {open && (
                <List
                  disablePadding
                  sx={{
                    position: 'relative',
                    '&:after': {
                      content: "''",
                      position: 'absolute',
                      left: '8px',
                      top: 0,
                      height: '100%',
                      width: '1px',
                      opacity: mode === ThemeMode.DARK ? 0.2 : 1,
                      bgcolor: mode === ThemeMode.DARK ? 'dark.light' : 'primary.light'
                    }
                  }}
                >
                  {menus}
                </List>
              )}
            </Collapse>
          )}
        </>
      ) : (
        <ListItemButton
          id={`boundary-${popperId}`}
          disableRipple
          selected={isSelected}
          onMouseEnter={handleHover}
          onMouseLeave={handleClosePopper}
          onClick={handleHover}
          aria-describedby={popperId}
        >
          {menuIcon && <ListItemIcon sx={{ my: 'auto', minWidth: !menu.icon ? 0 : 36 }}>{menuIcon}</ListItemIcon>}
          <ListItemText
            sx={{ mb: 0.25 }}
            primary={
              <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit" sx={{ my: 'auto' }}>
                {menu.title ? <FormattedMessage id={menu.title} /> : ''}
              </Typography>
            }
          />
          <IconChevronRight stroke={1.5} size="16px" />

          {anchorEl && (
            <PopperStyled
              id={popperId}
              open={openMini}
              anchorEl={anchorEl}
              placement="right-start"
              style={{
                zIndex: 2001
              }}
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [-10, 0]
                  }
                }
              ]}
            >
              {({ TransitionProps }) => (
                <Transitions in={openMini} {...TransitionProps}>
                  <Paper
                    onMouseLeave={handleClosePopper}
                    sx={{
                      overflow: 'hidden',
                      mt: 1.5,
                      py: 0.5,
                      boxShadow: theme.shadows[8],
                      backgroundImage: 'none'
                    }}
                  >
                    <ClickAwayListener onClickAway={handleClosePopper}>
                      <Box>{menus}</Box>
                    </ClickAwayListener>
                  </Paper>
                </Transitions>
              )}
            </PopperStyled>
          )}
        </ListItemButton>
      )
      }
    </>
  );
}

NavCollapse.propTypes = { menu: PropTypes.any, level: PropTypes.number, parentId: PropTypes.string };
